import React, { Suspense, lazy } from 'react';
import { 
  CssBaseline, 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Avatar,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Routes, 
  Route, 
  Link, 
  Navigate,
  useLocation
} from 'react-router-dom';
import { JobProvider } from './contexts/JobContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/auth/PrivateRoute';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HomeIcon from '@mui/icons-material/Home';

// Lazy load components
const JobForm = lazy(() => import('./components/JobForm'));
const JobList = lazy(() => import('./components/JobList'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Login = lazy(() => import('./components/auth/Login'));
const Signup = lazy(() => import('./components/auth/Signup'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const Profile = lazy(() => import('./components/auth/Profile'));

// Error Boundary Component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Alert severity="error">
            Something went wrong. Please refresh the page or try again later.
          </Alert>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Loading Component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
    <CircularProgress />
  </Box>
);

// Navigation Component
const Navigation = () => {
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [error, setError] = React.useState('');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (err) {
      setError('Failed to log out');
    }
  };

  return (
    <AppBar position="static" elevation={1}>
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              mr: 2,
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                textDecoration: 'none',
              }
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <HomeIcon />
            </Box>
            Job Tracker
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {currentUser ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Avatar 
                  alt={currentUser.displayName || 'User'} 
                  src={currentUser.photoURL || undefined}
                  sx={{ width: 32, height: 32 }}
                >
                  {currentUser.displayName?.[0] || currentUser.email?.[0] || <PersonIcon />}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem 
                  component={Link} 
                  to="/profile" 
                  onClick={handleClose}
                  selected={location.pathname === '/profile'}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={Link}
                to="/login"
                color="inherit"
                startIcon={<LoginIcon />}
                sx={{ textTransform: 'none' }}
              >
                Login
              </Button>
              <Button
                component={Link}
                to="/signup"
                variant="outlined"
                color="inherit"
                startIcon={<PersonAddIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError('')} 
          sx={{ 
            borderRadius: 0,
            '& .MuiAlert-message': {
              width: '100%',
              textAlign: 'center'
            }
          }}
        >
          {error}
        </Alert>
      )}
    </AppBar>
  );
};



function App() {
  const [error, setError] = React.useState<string | null>(null);
  const theme = useTheme(); // Get theme from context
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <ErrorBoundary>
      <AuthProvider>
        <JobProvider>
          <CssBaseline />
          <Navigation />
          
          <Box 
            component="main" 
            sx={{ 
              minHeight: 'calc(100vh - 64px)',
              backgroundColor: 'background.default',
              pt: isMobile ? 2 : 4,
              pb: 8
            }}
          >
            <Container maxWidth="lg">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route 
                    path="/login" 
                    element={
                      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                        <Login />
                      </Box>
                    } 
                  />
                  
                  <Route 
                    path="/signup" 
                    element={
                      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                        <Signup />
                      </Box>
                    } 
                  />
                  
                  <Route 
                    path="/forgot-password" 
                    element={
                      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                        <ForgotPassword />
                      </Box>
                    } 
                  />
                  
                  <Route 
                    path="/profile" 
                    element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    } 
                  />
                  
                  <Route 
                    path="/" 
                    element={
                      <PrivateRoute>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <JobForm />
                          <Dashboard />
                          <JobList />
                        </Box>
                      </PrivateRoute>
                    } 
                  />
                  
                  <Route 
                    path="*" 
                    element={<Navigate to="/" replace />} 
                  />
                </Routes>
              </Suspense>
            </Container>
          </Box>
          
          {/* Global Error Snackbar */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseError} 
              severity="error" 
              sx={{ 
                width: '100%',
                '& .MuiAlert-message': {
                  width: '100%',
                }
              }}
            >
              {error}
            </Alert>
          </Snackbar>
          
          {/* Footer */}
          <Box 
            component="footer" 
            sx={{ 
              py: 3, 
              mt: 'auto',
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Container maxWidth="lg">
              <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center"
                sx={{ fontSize: '0.875rem' }}
              >
                © {new Date().getFullYear()} Job Application Tracker. All rights reserved.
              </Typography>
            </Container>
          </Box>
        </JobProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;